import { useEffect, useState, type FormEvent } from "react";
import { accountMessage, syncLabel } from "../account/messages";
import { SheetOverlay } from "./SheetOverlay";
import type { AccountActions, AccountView, ConsentActions, ConsentView } from "./view-model";

export interface AccountSheetProps {
  account?: AccountView;
  actions?: AccountActions;
  consent?: ConsentView;
  consentActions?: ConsentActions;
  initialTab?: "signin" | "signup";
  currentYear?: number;
  onClose(): void;
}

const MONTHS = [
  { value: "1", label: "January" },
  { value: "2", label: "February" },
  { value: "3", label: "March" },
  { value: "4", label: "April" },
  { value: "5", label: "May" },
  { value: "6", label: "June" },
  { value: "7", label: "July" },
  { value: "8", label: "August" },
  { value: "9", label: "September" },
  { value: "10", label: "October" },
  { value: "11", label: "November" },
  { value: "12", label: "December" },
];

function getYearList(currentYear: number): number[] {
  const years: number[] = [];
  for (let y = currentYear; y >= currentYear - 100; y--) {
    years.push(y);
  }
  return years;
}

function formatExpiryTime(expiresAt: string): string {
  try {
    const ms = Date.parse(expiresAt);
    if (Number.isNaN(ms)) return "";
    return new Intl.DateTimeFormat([], { hour: "2-digit", minute: "2-digit" }).format(ms);
  } catch {
    return "";
  }
}

export function AccountSheet({
  account,
  actions,
  consent,
  consentActions,
  initialTab,
  currentYear,
  onClose,
}: AccountSheetProps) {
  const initialMode =
    account?.firstTime !== undefined
      ? (account.firstTime ? "create" : "signin")
      : (initialTab === "signup" ? "create" : "signin");
  const [mode, setMode] = useState<"signin" | "create">(initialMode);
  const [showPassword, setShowPassword] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [birthMonth, setBirthMonth] = useState("");
  const [birthYear, setBirthYear] = useState("");
  const [googleMonth, setGoogleMonth] = useState("");
  const [googleYear, setGoogleYear] = useState("");
  const [localProblem, setLocalProblem] = useState<string | null>(null);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");
  const [copied, setCopied] = useState(false);

  const closeInnerView = (): boolean => {
    if (deleteOpen) {
      setDeleteOpen(false);
      setDeletePassword("");
      setLocalProblem(null);
      return true;
    }
    if (!account?.signedIn && mode !== initialMode) {
      setMode(initialMode);
      setLocalProblem(null);
      return true;
    }
    return false;
  };

  useEffect(() => {
    if (account?.signedIn && consentActions) {
      consentActions.onLoadConsent().catch(() => {});
    }
  }, [account?.signedIn, consentActions]);

  async function handleCopy() {
    if (!consent?.approvalLink) return;
    try {
      await navigator.clipboard.writeText(consent.approvalLink.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore failure
    }
  }

  const defaultYear =
    typeof Intl !== "undefined"
      ? Number(new Intl.DateTimeFormat("en-US", { year: "numeric" }).format())
      : 2026;
  const resolvedYear = currentYear ?? defaultYear;
  const years = getYearList(resolvedYear);

  const effectiveProblem = localProblem ?? account?.problem ?? null;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!actions || !account || account.busy) return;
    const currentPass = password;
    setPassword("");
    setLocalProblem(null);

    if (mode === "signin") {
      if (!username.trim() || !currentPass) return;
      const code = await actions.onSignIn(username.trim(), currentPass);
      if (code) {
        setLocalProblem(code);
      }
    } else {
      if (!username.trim() || !currentPass || !birthMonth || !birthYear) return;
      const code = await actions.onSignUp(username.trim(), currentPass, {
        year: Number(birthYear),
        month: Number(birthMonth),
      });
      if (code) {
        setLocalProblem(code);
      }
    }
  }

  async function handleGoogleComplete() {
    if (!actions?.onGoogleComplete || !googleMonth || !googleYear || account?.busy) return;
    setLocalProblem(null);
    const code = await actions.onGoogleComplete(Number(googleMonth), Number(googleYear));
    if (code) {
      setLocalProblem(code);
    }
  }

  async function handleDownload() {
    if (!actions || !account || account.busy) return;
    setLocalProblem(null);
    const code = await actions.onDownloadData();
    if (code) {
      setLocalProblem(code);
    }
  }

  async function handleDelete() {
    if (!actions || !account || account.busy || !deletePassword) return;
    const currentPass = deletePassword;
    setDeletePassword("");
    setLocalProblem(null);
    const code = await actions.onDeleteAccount(currentPass);
    if (code) {
      setLocalProblem(code);
    }
  }

  const isSubmitDisabled =
    !account ||
    account.busy ||
    !username.trim() ||
    !password ||
    (mode === "create" && (!birthMonth || !birthYear));

  return (
    <SheetOverlay
      testId="account-sheet"
      label="Account"
      title={account?.signedIn ? "Account" : "Save my game"}
      onClose={onClose}
      onEscape={closeInnerView}
    >
      <div className="sb">
        {!account || !actions ? (
          <div className="note">Accounts are not available right now.</div>
        ) : account.signedIn && account.status === "conflict" ? (
          <>
            <h4>Two different games found</h4>
            <div className="note">
              This device and your account each have a game. Pick the one to keep. The other one is
              replaced and can't be brought back.
            </div>

            {effectiveProblem ? (
              <div className="note" role="alert" data-testid="account-problem">
                {accountMessage(effectiveProblem)}
              </div>
            ) : null}

            <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "8px" }}>
              <button
                className="btn"
                type="button"
                data-testid="account-conflict-cloud"
                disabled={account.busy}
                onClick={() => actions.onResolveConflict("cloud")}
              >
                Use the account's game
              </button>
              <button
                className="btn alt"
                type="button"
                data-testid="account-conflict-device"
                disabled={account.busy}
                onClick={() => actions.onResolveConflict("device")}
              >
                Keep this device's game
              </button>
            </div>
          </>
        ) : account.signedIn ? (
          <>
            <div className="row">
              <div
                className="grow"
                style={{ overflowWrap: "anywhere", wordBreak: "break-word" }}
              >
                <span data-testid="account-username-shown">{account.username}</span>
                <small>Only you can see this</small>
              </div>
            </div>

            <div className="row">
              <div className="grow">
                <span data-testid="account-status">{syncLabel(account.status)}</span>
              </div>
            </div>

            {effectiveProblem ? (
              <div className="note" role="alert" data-testid="account-problem">
                {accountMessage(effectiveProblem)}
              </div>
            ) : null}

            {consent ? (
              <div
                data-testid="consent-section"
                style={{
                  display: "grid",
                  gridTemplateColumns: "minmax(0, 1fr)",
                  gap: "10px",
                }}
              >
                <h4>Google and buying</h4>

                {consent.google ? (
                  <div className="row">
                    <div className="grow">
                      <span data-testid="google-connected">Google is connected</span>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "6px" }}>
                    <button
                      className="btn"
                      type="button"
                      data-testid="google-connect"
                      disabled={consent.busy}
                      onClick={() => consentActions?.onConnectGoogle()}
                    >
                      Connect to Google
                    </button>
                    <div className="note" data-testid="google-note">
                      Connecting Google lets you get back into your account and is needed before buying. We only get a private number from Google, never your email or name.
                    </div>
                  </div>
                )}

                {consent.googleResult ? (
                  <div
                    className="row"
                    data-testid="google-result"
                    role={consent.googleResult === "linked" ? "status" : "alert"}
                  >
                    <div className="grow">
                      {consent.googleResult === "linked"
                        ? "Google is connected."
                        : "Connecting Google didn't work. Please try again."}
                    </div>
                    <button
                      className="btn alt"
                      type="button"
                      data-testid="google-result-dismiss"
                      onClick={() => consentActions?.onDismissGoogleResult()}
                    >
                      OK
                    </button>
                  </div>
                ) : null}

                {consent.approval !== "not_needed" ? (
                  <div
                    data-testid="approval-section"
                    style={{
                      display: "grid",
                      gridTemplateColumns: "minmax(0, 1fr)",
                      gap: "8px",
                    }}
                  >
                    <div className="row">
                      <div className="grow">
                        <span data-testid="approval-status">
                          {consent.approval === "approved"
                            ? "A parent has said OK."
                            : "A parent needs to say OK before you can buy."}
                        </span>
                      </div>
                    </div>

                    <button
                      className="btn"
                      type="button"
                      data-testid="approval-make-link"
                      disabled={consent.busy}
                      onClick={() => consentActions?.onMakeApprovalLink()}
                    >
                      {consent.approvalLink ? "Make a new link" : "Make a link for a parent"}
                    </button>

                    {consent.approvalLink ? (
                      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "6px" }}>
                        <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                          <div className="fi" style={{ flex: 1, minWidth: 0 }}>
                            <input
                              id="approval-link"
                              data-testid="approval-link"
                              type="text"
                              readOnly
                              aria-label="Link for a parent"
                              value={consent.approvalLink.url}
                              onFocus={(e) => e.target.select()}
                              style={{ minWidth: 0 }}
                            />
                          </div>
                          <button
                            className="btn alt"
                            type="button"
                            data-testid="approval-copy"
                            onClick={handleCopy}
                            style={{ flex: "none" }}
                          >
                            {copied ? "Copied" : "Copy"}
                          </button>
                        </div>
                        <div className="note" data-testid="approval-expiry">
                          {`Works until ${formatExpiryTime(consent.approvalLink.expiresAt)} for one parent, one time`}
                        </div>
                      </div>
                    ) : null}

                    <div className="note" data-testid="approval-note">
                      Give this link to a parent. They sign in with their own Google account to say OK. It shows that an adult is in charge; it is not a legal age check.
                    </div>
                  </div>
                ) : null}

                <div className="row col" data-testid="buying-status">
                  <div className="grow">
                    {consent.canBuy ? (
                      "You can buy things."
                    ) : (
                      <>
                        <span>To buy things you still need:</span>
                        <ul data-testid="buying-missing" style={{ margin: "4px 0 0 18px", padding: 0 }}>
                          {consent.missing.map((item) => (
                            <li key={item}>
                              {item === "google" ? "Connect to Google" : "A parent's OK"}
                            </li>
                          ))}
                        </ul>
                      </>
                    )}
                  </div>
                </div>

                {consent.problem ? (
                  <div className="note" role="alert" data-testid="consent-problem">
                    {accountMessage(consent.problem)}
                  </div>
                ) : null}
              </div>
            ) : null}

            {!deleteOpen ? (
              <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "8px" }}>
                <button
                  className="btn alt"
                  type="button"
                  data-testid="account-signout"
                  disabled={account.busy}
                  onClick={() => actions.onSignOut()}
                >
                  Sign out
                </button>
                <button
                  className="btn alt"
                  type="button"
                  data-testid="account-download"
                  disabled={account.busy}
                  onClick={handleDownload}
                >
                  Download my data
                </button>
                <button
                  className="btn alt"
                  type="button"
                  data-testid="account-delete-start"
                  disabled={account.busy}
                  onClick={() => {
                    setDeleteOpen(true);
                    setDeletePassword("");
                    setLocalProblem(null);
                  }}
                >
                  Delete my account
                </button>
              </div>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "8px" }}>
                <label htmlFor="account-delete-password">{account.viaGoogle ? "Type your username to confirm" : "Your password"}</label>
                <div className="fi">
                  <input
                    id="account-delete-password"
                    data-testid="account-delete-password"
                    type={account.viaGoogle ? "text" : "password"}
                    autoComplete={account.viaGoogle ? "off" : "current-password"}
                    value={deletePassword}
                    onChange={(e) => setDeletePassword(e.target.value)}
                  />
                </div>
                <div className="note">
                  This deletes your account and the game saved in it. The game on this device stays.
                </div>
                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                  <button
                    className="btn"
                    type="button"
                    data-testid="account-delete-confirm"
                    disabled={account.busy || !deletePassword}
                    onClick={handleDelete}
                  >
                    Delete everything
                  </button>
                  <button
                    className="btn alt"
                    type="button"
                    data-testid="account-delete-cancel"
                    onClick={() => {
                      setDeleteOpen(false);
                      setDeletePassword("");
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </>
        ) : account.googlePending ? (
          <div
            data-testid="google-complete"
            style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "10px" }}
          >
            <h4>One more step</h4>
            <div className="note">
              We ask for your birth month and year to finish setting up your account. We only check that you can play.
            </div>

            {effectiveProblem ? (
              <div className="note" role="alert" data-testid="account-problem">
                {accountMessage(effectiveProblem)}
              </div>
            ) : null}

            <div>
              <label htmlFor="google-birth-month">Birth month</label>
              <div className="fi">
                <select
                  id="google-birth-month"
                  data-testid="google-birth-month"
                  value={googleMonth}
                  onChange={(e) => setGoogleMonth(e.target.value)}
                >
                  <option value="">Choose</option>
                  {MONTHS.map((m) => (
                    <option key={m.value} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label htmlFor="google-birth-year">Birth year</label>
              <div className="fi">
                <select
                  id="google-birth-year"
                  data-testid="google-birth-year"
                  value={googleYear}
                  onChange={(e) => setGoogleYear(e.target.value)}
                >
                  <option value="">Choose</option>
                  {years.map((y) => (
                    <option key={y} value={String(y)}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <button
              className="btn"
              type="button"
              data-testid="google-complete-submit"
              disabled={account.busy || !googleMonth || !googleYear}
              onClick={handleGoogleComplete}
            >
              Finish
            </button>
          </div>
        ) : (
          <div className="save-screen">
            <div className="note">
              Play on this device first. Save anytime to play on your phone, tablet or computer.
            </div>

            <button
              className="google-btn"
              type="button"
              data-testid="google-signin"
              disabled={account.busy || account.googleAvailable === false}
              onClick={() => actions.onGoogleSignIn?.()}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true" width="18" height="18">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"
                />
              </svg>
              Continue with Google
            </button>

            {account.googleAvailable === false ? (
              <div className="note" data-testid="google-disabled-note">
                Google sign-in is not available yet
              </div>
            ) : null}

            <div className="divider">
              <span>or use a username</span>
            </div>

            <div style={{ textAlign: "center" }}>
              <button
                type="button"
                className="link-btn"
                data-testid="account-mode-toggle"
                onClick={() => {
                  setMode((m) => (m === "create" ? "signin" : "create"));
                  setLocalProblem(null);
                }}
              >
                {mode === "create" ? "Already have an account? Sign in" : "Need an account? Create one"}
              </button>
            </div>

            <form
              onSubmit={handleSubmit}
              style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "10px" }}
            >
              <div>
                <label htmlFor="account-username">Username</label>
                <div className="fi">
                  <input
                    id="account-username"
                    data-testid="account-username"
                    autoComplete="username"
                    autoCapitalize="none"
                    maxLength={20}
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="account-password">Password</label>
                <div className="fi pass-box">
                  <input
                    id="account-password"
                    data-testid="account-password"
                    type={showPassword ? "text" : "password"}
                    autoComplete={mode === "create" ? "new-password" : "current-password"}
                    autoCapitalize="none"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    className="pass-toggle"
                    data-testid="account-password-toggle"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    onClick={() => setShowPassword((s) => !s)}
                  >
                    {showPassword ? "Hide" : "Show"}
                  </button>
                </div>
              </div>

              {mode === "create" ? (
                <>
                  <div>
                    <label htmlFor="account-birth-month">Birth month</label>
                    <div className="fi">
                      <select
                        id="account-birth-month"
                        data-testid="account-birth-month"
                        value={birthMonth}
                        onChange={(e) => setBirthMonth(e.target.value)}
                      >
                        <option value="">Choose</option>
                        {MONTHS.map((m) => (
                          <option key={m.value} value={m.value}>
                            {m.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="account-birth-year">Birth year</label>
                    <div className="fi">
                      <select
                        id="account-birth-year"
                        data-testid="account-birth-year"
                        value={birthYear}
                        onChange={(e) => setBirthYear(e.target.value)}
                      >
                        <option value="">Choose</option>
                        {years.map((y) => (
                          <option key={y} value={String(y)}>
                            {y}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </>
              ) : null}

              {effectiveProblem ? (
                <div className="note" role="alert" data-testid="account-problem">
                  {accountMessage(effectiveProblem)}
                </div>
              ) : null}

              <button
                className="btn"
                type="submit"
                data-testid="account-submit"
                disabled={isSubmitDisabled}
              >
                {mode === "create" ? "Create account" : "Sign in"}
              </button>

              <div className="note">Your username is private. Other players never see it.</div>

              {mode === "create" ? (
                <>
                  <div className="note">
                    We ask for your birth month and year only to check you can play. We don't keep
                    your birthday.
                  </div>
                  <div className="note">Use a few words together as a password.</div>
                </>
              ) : null}
            </form>
          </div>
        )}
      </div>
    </SheetOverlay>
  );
}
